import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import InputSelect from '../../common/components/forms/InputSelect'
import InputSelectOption from '../../common/components/forms/InputSelectOption'
import RestrictionMessage from '../../common/components/RestrictionMessage'
import { OutlineButton } from '../../common/styled-components/buttons'
import { Column, Row } from '../../common/styled-components/layout'
import { BoldParagraph, H1, H2, Paragraph, PrecisionParagraph } from '../../common/styled-components/typography'
import FamilyMember from '../../dtos/FamilyMember'
import Offer from '../../dtos/Offer'
import { IOfferOption } from '../../dtos/Wish'
import { familyMembersToSelectOptions, restrictions } from '../../utils/qidigo'
import {getHtmlContent } from '../../utils/text'
import Warning from '../../common/components/Warning'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useDateFormats } from '../../hooks/useDateFormats'
import {IRestrictions} from "../wishlist/types";
import MoneyDisplay from "../../common/components/MoneyDisplay";

const groupsAllHaveWaitingLists = (groups: { waiting_list: boolean }[]) => !groups.find((group) => !group.waiting_list);

interface OfferItemProps {
    offer: Offer,
    familyMembers: { [key: string]: Partial<FamilyMember> },
    onMemberSelected: (option: IOfferOption | undefined) => void
    refreshOptions?: () => Promise<unknown>
    shouldRenderMembershipOptions?: boolean
    shouldHideInfo?: boolean
    activityId: string
    organizationSlug: string | undefined
    toggleShoppingButtonDisability: (val: boolean) => void
    isAddedToWaitingList: boolean
}

const OfferItem: React.FC<OfferItemProps> = ({
  offer,
  familyMembers,
  onMemberSelected,
  refreshOptions,
  shouldRenderMembershipOptions = false,
  shouldHideInfo = false,
  activityId,
  organizationSlug,
  toggleShoppingButtonDisability,
  isAddedToWaitingList
}) => {
    const {t} = useTranslation();

    const membersOptions = familyMembersToSelectOptions(t, familyMembers);

    const [selectedMember, setSelectedMember] = useState<InputSelectOption<number> | null>(null);
    const [selectedOption, setSelectedOption] = useState(selectedMember && offer.options.find((opt) => opt.subscriber === selectedMember.value));

    useEffect(() => {
        if (selectedMember) {
          setSelectedOption(offer.options.find((opt) => opt.subscriber === selectedMember.value));
        }
    }, [offer, selectedMember?.value]);

    const onMemberSelect = useCallback((member: InputSelectOption<number>) => {
        setSelectedMember(member);
        const selectedOption = offer.options.find((option) => option.subscriber === member.value)
        setSelectedOption(selectedOption);
        onMemberSelected(selectedOption)
    }, [offer]);

    const hasMultipleGroups = offer.plan.groups.length > 1;
    const isMembership = offer.plan.membership_template_id !== null;

    const { shortFormat, longFormat } = useDateFormats()

    const formattedGroupStartDate = !hasMultipleGroups ? moment(offer.plan.groups[0] ? offer.plan.groups[0].start_date : Date.now()).format(shortFormat) : null;
    const offerDescription = hasMultipleGroups ? t('offers.sessionOfferDescription$numberOfGroupsGroups', {numberOfGroups: offer.plan.groups.length}) : t('offers.sessionOfferDescriptionSingleGroup', {startDate: formattedGroupStartDate});

    const makeListOfRestrictionCodes = (restrictions: IRestrictions) => {
        let groupRestrictions: never[] = [];
        restrictions.groups_restrictions.map((groupRestriction) => groupRestriction.restrictions)
            .forEach((restrictions) => {
                groupRestrictions = groupRestrictions.concat(restrictions.filter((restriction: any) => {
                    return groupRestrictions.find(
                        (addedRestriction) => addedRestriction === restriction
                    ) === undefined;
                }));
            });

        const productMembershipRestrictions = restrictions.product_restrictions.filter((restriction) => {
            return restriction.memberships !== undefined;
        })

        const productRestrictions =  restrictions.product_restrictions.filter((restriction) => {
            return restriction.memberships === undefined;
        }).map((restriction) => {return restriction.code});


        return ([] as (any | never)[]).concat(
            restrictions.organization_restrictions,
            restrictions.registration_restrictions,
            restrictions.membership_restrictions,
            productRestrictions,
            groupRestrictions,
            productMembershipRestrictions
        );
    }

    const restrictionFlatten = selectedOption ? makeListOfRestrictionCodes(selectedOption.restrictions) : [];

    const waitingListAvailable = selectedOption && restrictionFlatten.length === 1 && restrictionFlatten[0] === restrictions.groupIsFull && groupsAllHaveWaitingLists(offer.plan.groups);
    const isAlreadyOnWaitingList = !!(selectedOption && selectedOption.on_waiting_list);
    const canRegisterOnWaitingList = waitingListAvailable && !isAlreadyOnWaitingList;

    const isTooSoonToRegister = selectedOption && !!selectedOption.start_date ? moment(selectedOption.start_date).isAfter(moment()) : false;
    const isTooLateToRegister = selectedOption && !!selectedOption.end_date ? moment(selectedOption.end_date).isBefore(moment()) : false;

    let descriptionComponents = [];

    if (!shouldHideInfo && isMembership) {
      descriptionComponents.push(
        <Paragraph key={descriptionComponents.length}>
          {t('offers.hasMembership')}
        </Paragraph>
      );
    }

    if (Boolean(offer.plan.description)) {
      descriptionComponents.push(
        <Paragraph
          key={descriptionComponents.length}
          dangerouslySetInnerHTML={{ __html: getHtmlContent(offer.plan.description || '') }}
        />);
    }
    if (isTooSoonToRegister) {
      descriptionComponents.push(<PrecisionParagraph key={descriptionComponents.length}>{t('offers.registration$startDate', {startDate: moment(selectedOption?.start_date).format(longFormat)})}</PrecisionParagraph>);
    }
    if (!isTooLateToRegister && selectedOption && !!selectedOption.end_date) {
      descriptionComponents.push(<PrecisionParagraph key={descriptionComponents.length}>{t('offers.registration$endDate', {endDate: moment(selectedOption.end_date).format(longFormat)})}</PrecisionParagraph>);
    }

    let offerComponents = [];
    if (isAlreadyOnWaitingList) {
        offerComponents.push(<Paragraph key={offerComponents.length}>{t('offers.alreadyOnWaitingList')}</Paragraph>)
    } else if (!canRegisterOnWaitingList && selectedOption && restrictionFlatten.length > 0) {
        offerComponents.push(
          <RestrictionMessageContainer key={offerComponents.length}>
            <RestrictionMessage
              shouldRenderMembershipOptions={shouldRenderMembershipOptions}
              selectedMemberId={selectedOption && String(selectedOption.subscriber)}
              restrictions={restrictionFlatten}
              multipleGroups={hasMultipleGroups}
              group={hasMultipleGroups ? undefined : offer.plan.groups[0]}
              refreshOptions={refreshOptions}
              toggleShoppingButtonDisability={toggleShoppingButtonDisability}
            />
          </RestrictionMessageContainer>
        )
    } else if (!isTooSoonToRegister && !isTooLateToRegister) {
      offerComponents.push(
        selectedOption &&
          <PriceAndWarningContainer key={offerComponents.length}>
              {
                  canRegisterOnWaitingList &&
                  <WaitingMessageContainer>
                      <Warning messages={[t('offers.canRegisterOnWaitingList')]}/>
                  </WaitingMessageContainer>
              }

              <PriceContainer style={{width: canRegisterOnWaitingList ? '25%' : undefined}}>
                  <Price><MoneyDisplay amount={selectedOption.price}/></Price>
                  <PricePrecision>({t('feesAndTaxesIncluded')})</PricePrecision>
              </PriceContainer>
          </PriceAndWarningContainer>
      )
    }

    // @ts-ignore
    const selectInput = <InputSelect
      value={selectedMember}
      options={membersOptions}
      onChange={onMemberSelect}
      placeholder={t('wishlist.selectMember')}
      withCaret
    />

    return (
        <SessionOfferContainer>
            {
              !shouldHideInfo &&
              <H2 className={"plan-title"}>{offer.plan.name}</H2>
            }
            <Column>{descriptionComponents}</Column>
            {offer && offer.plan.type_plan === 'PASS' ?
              (<RedirectionMessageContainer>
                <RedirectionMessage>{t('offers.redirectMessage')}</RedirectionMessage>
                <RedirectionLink href={`${process.env.REACT_APP_FRONT_END_URL}/u/${organizationSlug}/activity/${activityId}/schedule`} target='_blank' rel='noopener noreferrer'>
                  {t('offers.redirectLink')}
                </RedirectionLink>
              </RedirectionMessageContainer>) : (
              <MemberSelectAndOfferContainer>
                <MemberSelectContainer>{ selectInput }</MemberSelectContainer>
                <OfferContainer>
                  {
                    isAddedToWaitingList ?
                      (<AddedToWaitingListMessage>{t('offers.userSubscribedToWaitingList')}</AddedToWaitingListMessage>) : (offerComponents)
                  }
                </OfferContainer>
              </MemberSelectAndOfferContainer>
            )}
        </SessionOfferContainer>
    )
};

const SessionOfferContainer = styled(Column)`
  width: 100%;
`;

const MemberSelectAndOfferContainer = styled(Row)`
  width: 100%;
  align-items: center;
  flex-wrap: wrap;
  min-height: 80px;

  @media only screen and (max-width: 767px) {
    flex-direction: column;
    height: auto;
  }
`;

const MemberSelectContainer = styled.div`
  flex: 1;
  min-width: 250px;
  max-width: 300px;
  padding-right: 100px;
  div {
    margin-bottom: 0;
  }
  @media only screen and (max-width: 767px) {
    margin-bottom: 30px;
    padding-right: 0;
  }
`;

const OfferContainer = styled(Row)`
  flex: 1;
  align-items: flex-end;
  justify-content: flex-end;

  @media only screen and (max-width: 767px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PriceAndWarningContainer = styled(Row)`
  align-items: flex-end;
  margin-right: 40px;

  @media only screen and (max-width: 767px) {
    margin-right: 0;
    margin-bottom: 30px;
  }
`;
const Price = styled(H1)`
  margin: 0 10px 0 0 !important;
  line-height: 0.9;
`;
const PricePrecision = styled(BoldParagraph)`
  margin: 0;
`;
const RestrictionMessageContainer = styled.div`
  flex: 1;
`;

const RemoveFromCartButton = styled(OutlineButton)`
  margin-right: 20px;
`;

const RedirectionMessageContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
  text-align: center;
`
const RedirectionLink = styled.a`
  font-size: 17px;
  color: black;
  text-decoration: none;
  font-weight: bold;
`
const RedirectionMessage = styled.p`
  font-size: 17px;
  display: inline-block;
  margin: 0px 2px 0px 0px;
`
const AddedToWaitingListMessage = styled.p`
  font-size: 17px;
  margin: 8px 7px 0px 0px;
  font-weight: 800;
`
const WaitingMessageContainer = styled.div`
  margin-right: 40px;
  width: 75%;
`
const PriceContainer = styled.div`
  text-align: end;
`
export default OfferItem;
